import {Context} from "react";
import {z, ZodType} from "zod/v4";
import {faSkullCow} from "@fortawesome/pro-regular-svg-icons";
import {FontAwesomeIconProps} from "@fortawesome/react-fontawesome";

export interface Triggerable {
    trigger?: () => void
}

export function removeTrigger<T extends Triggerable>(prev: T): T {
    if (!prev.trigger) {
        return prev;
    }
    const next = {...prev};
    delete next.trigger;
    return next;
}

// this is potentially dangerous if you want empty strings to be legal values
// as they will be stripped even if they are required fields by the type.
// export function removeBlanks<T>(from: T): T {
//     return Object.fromEntries(Object.entries(from as object)
//         .filter(([_, v]) => !isEmpty(v))) as T;
// }

export type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>;
};

export interface ContextProviderProps<T> {
    context: Context<T>;
    value: T;
    displayName: string;
}

// export function mergeContextProviders(...providerProps: ContextProviderProps<any>[]) {
//     return providerProps.reduce<({children}: PropsWithChildren) => ReactNode>((Acc: ({children}: PropsWithChildren) => ReactNode, next: ContextProviderProps<any>) => {
//         // eslint-disable-next-line react/display-name
//         return ({children}: PropsWithChildren) => {
//             return <Acc>
//                 {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
//                 <next.context value={next.value}>{children}</next.context>
//             </Acc>;
//         }
//     }, ({children}: PropsWithChildren) => <>{children}</>);
// }

export const PendingUseResult = z.object({
    status: z.literal("pending"),
    isPending: z.literal(true),
    isSuccess: z.literal(false),
    isError: z.literal(false),
    data: z.optional(z.undefined()),
});
export type PendingUseResult = z.infer<typeof PendingUseResult>;

export const pendingUseResult: PendingUseResult = {
    status: "pending",
    isPending: true,
    isSuccess: false,
    isError: false
} as const;

export const BaseSuccessUseResult = z.object({
    status: z.literal("success"),
    isPending: z.literal(false),
    isSuccess: z.literal(true),
    isError: z.literal(false),
});
export type SuccessUseResult<T> = z.infer<typeof BaseSuccessUseResult> & { data: T };

export function makeSuccessUseResultSchema<T extends ZodType>(data: T) {
    return BaseSuccessUseResult.extend({data: data});
}

export function successUseResult<T>(data: T): SuccessUseResult<T> {
    return {data, status: "success", isPending: false, isSuccess: true, isError: false} as const;
}

export function makeInfallibleUseResult<T extends ZodType>(dataSchema: T) {
    return z.discriminatedUnion("status",
        [PendingUseResult, makeSuccessUseResultSchema(dataSchema)]);
}

export type InfallibleUseResult<T> = PendingUseResult | SuccessUseResult<T>;

export const BaseErrorUseResult = z.object({
    status: z.literal("error"),
    isPending: z.literal(false),
    isSuccess: z.literal(false),
    isError: z.literal(true),
});
export type ErrorUseResult<ErrorT> = z.infer<typeof BaseErrorUseResult> & { error: ErrorT };

export function makeErrorUseResultSchema<ErrorT extends ZodType>(errorSchema: ErrorT) {
    return BaseErrorUseResult.extend({error: errorSchema});
}

export function errorUseResult<ErrorT>(error: ErrorT): ErrorUseResult<ErrorT> {
    return {status: "error", error, isPending: false, isSuccess: false, isError: true} as const;
}

export function makeFallibleUseResultSchema<T extends ZodType, ErrorT extends ZodType>(dataSchema: T, errorSchema: ErrorT) {
    return z.discriminatedUnion("status", [
        PendingUseResult,
        makeSuccessUseResultSchema(dataSchema),
        makeErrorUseResultSchema(errorSchema),
    ]);
}

export type FallibleUseResult<T, ErrorT> = PendingUseResult | SuccessUseResult<T> | ErrorUseResult<ErrorT>;

export function transformUseResult(args: { from: PendingUseResult, via: (from: unknown) => unknown }): PendingUseResult;
export function transformUseResult<ErrorT>(args: {
    from: ErrorUseResult<ErrorT>,
    via: (from: unknown) => unknown
}): ErrorUseResult<ErrorT>;
export function transformUseResult<FromT, ToT>({from, via}: {
    from: SuccessUseResult<FromT>,
    via: (from: FromT) => ToT
}): SuccessUseResult<ToT>;
export function transformUseResult<FromT, ToT>({from, via}: {
    from: InfallibleUseResult<FromT>,
    via: (from: FromT) => ToT
}): InfallibleUseResult<ToT>;
export function transformUseResult<FromT, ToT, ErrorT>({from, via}: {
    from: FallibleUseResult<FromT, ErrorT>,
    via: (from: FromT) => ToT
}): FallibleUseResult<ToT, ErrorT>;
export function transformUseResult<FromT, ToT, ErrorT>({from, via}: {
    from: FallibleUseResult<FromT, ErrorT>,
    via: (from: FromT) => ToT
}): FallibleUseResult<ToT, ErrorT> {
    switch (from.status) {
        case "pending":
        case "error":
            return from;

        case "success":
            return {...from, data: via(from.data)};
    }
}

export const DEADBEEF = "DEADBEEF";
export const DEADBEEF_ICON: FontAwesomeIconProps = {icon: faSkullCow, flip: "vertical"} as const;

